<template>
  <a-dialog ref="dialog" name="confirm_payment" :loading="loading" @open="opened" @close="$reset">
    <span slot="title">{{ $t('title') }}</span>
    <div slot="content">
      <a-payment-method-form
        ref="payment_method"
        @valid="onPaymentValid"
        @message="onFormMessage">
      </a-payment-method-form>
    </div>
    <div slot="actions">
      <v-btn color="success" text :disabled="!valid" @click="submit()">
        <v-icon left>mdi-lock</v-icon>
        {{ $t('actions.pay') }} {{ $helpers.present.currency(payment.amount / 100) }}
      </v-btn>
    </div>
  </a-dialog>
</template>

<script>
export default {
  name: 'ConfirmPaymentDialog',
  data () {
    return {
      loading: '',
      valid: true,
      stripe: null,
      payment: {
        amount: null,
        client_secret: null,
        method: null,
      }
    }
  },
  methods: {
    onPaymentValid (valid) {
      this.valid = valid
    },
    onFormMessage (message) {
      this.loading = message
    },
    async opened () {
      const Stripe = await this.$stripe.load()
      this.stripe = Stripe(process.env.VUE_APP_STRIPE_API_KEY)

      try {
        this.loading = this.$t('loading')
        const response = await this.$api.get('account/customer/payment')
        this.payment.amount = response.data.amount
        this.payment.client_secret = response.data.client_secret
        this.payment.method = response.data.payment_method
      } finally {
        this.loading = ''
      }
    },
    submit () {
      this.loading = this.$t('payment-validation')

      this.$refs.payment_method.pay(this.payment.client_secret, this.payment.method)
        .then(() => this.waitForSubscriptionAndCloseDialog())
        .catch(err => {
          this.loading = false
          this.$ui.error(err)
        })
    },

    /*
     * COMMON ELEMENTS FOR PAYMENT MANAGMENT
     */

    // Dialog control / action outcome control
    closeDialogWithSuccess (message) {
      this.$ui.success(message)
      this.loading = ''
      this.closeDialog()
    },
    closeDialogWithError (message) {
      this.$ui.warning(message)
      this.loading = ''
      this.closeDialog()
    },
    closeDialogWithTimeout () {
      this.$ui.warning(this.$t('ui-warning'))
      this.loading = ''
      this.closeDialog()
    },
    closeDialog () {
      if (this.$refs.dialog) this.$refs.dialog.close()
    },

    // Helpers
    refreshAccountInformations () {
      this.loading = this.$t('ui-update')
      return new Promise(resolve => {
        this.$account.fetch({ showLoading: false }).then(() => resolve())
      })
    },
    waitForSubscriptionAndCloseDialog () {
      this.loading = this.$t('ui-loading')
      this.waitForSubscription()
        .then(success => {
          if (success) this.closeDialogWithSuccess(this.$t('ui-success'))
          else this.closeDialogWithTimeout()
        })
    },
    waitForSubscription () {
      return new Promise(resolve => {
        let i = 0

        const interval = setInterval(() => {
          if (i++ >= 4) {
            clearInterval(interval)
            resolve(false)
          } else {
            this.$account.fetch({ showLoading: false })
              .then(() => {
                const subscription = this.$account.getSubscription('check-position')
                if (subscription.name !== 'default' && subscription.status === 'active') {
                  clearInterval(interval)
                  resolve(true)
                }
              })
              .catch(() => {
                clearInterval(interval)
                resolve(false)
              })
          }
        }, 5 * 1000)
      })
    },
  }
}
</script>

<i18n>
{
	"en": {
		"loading": "Loading ...",
		"payment-validation": "Payment validation ...",
		"title": "Confirm Payment",
		"ui-loading": "Waiting for payment (please wait, it may take several seconds) ...",
		"ui-success": "Payment successfully validated!",
		"ui-update": "Updating your subscription ...",
		"ui-warning": "Payment is still pending, please wait a few minutes then refresh the page."
	},
	"es": {
		"loading": "Cargando ...",
		"payment-validation": "Validación de pago ...",
		"title": "Confirmar pago",
		"ui-loading": "Esperando el pago (espere, puede tardar varios segundos) ...",
		"ui-success": "Pago validado con éxito!",
		"ui-update": "Actualizando su suscripción ...",
		"ui-warning": "El pago aún está pendiente, espere unos minutos y luego actualice la página."
	},
	"fr": {
		"loading": "Chargement ...",
		"payment-validation": "Validation du paiement ...",
		"title": "Confirmer le paiement",
		"ui-loading": "En attente du paiement (merci de patienter, cela peut prendre plusieurs secondes) ...",
		"ui-success": "Paiement validé avec succès !",
		"ui-update": "Mise à jour de votre souscription ...",
		"ui-warning": "Le paiement est toujours en attente, merci de patienter quelques minutes puis actualiser la page."
	},
	"it": {
		"loading": "Caricamento ...",
		"payment-validation": "Convalida del pagamento ...",
		"title": "Conferma pagamento",
		"ui-loading": "In attesa del pagamento (attendi, potrebbe richiedere qualche secondo) ...",
		"ui-success": "Pagamento convalidato con successo!",
		"ui-update": "Aggiornamento del tuo abbonamento ...",
		"ui-warning": "Il pagamento è ancora in attesa, attendi qualche minuto e poi aggiorna la pagina."
	},
	"pt": {
		"loading": "A carregar ...",
		"payment-validation": "Validação do pagamento ...",
		"title": "Confirmar pagamento",
		"ui-loading": "À espera do pagamento (por favor aguarde, pode demorar alguns segundos) ...",
		"ui-success": "Pagamento validado com sucesso!",
		"ui-update": "A atualizar a sua subscrição ...",
		"ui-warning": "O pagamento ainda está pendente, por favor aguarde alguns minutos e depois atualize a página."
	},
	"pl": {
		"loading": "Ładowanie ...",
		"payment-validation": "Walidacja płatności ...",
		"title": "Potwierdź płatność",
		"ui-loading": "Oczekiwanie na płatność (prosimy o cierpliwość, może to potrwać kilka sekund) ...",
		"ui-success": "Płatność pomyślnie zatwierdzona!",
		"ui-update": "Aktualizacja Twojej subskrypcji ...",
		"ui-warning": "Płatność jest nadal oczekująca, poczekaj kilka minut, a następnie odśwież stronę."
	}
}
</i18n>
